import React, { useState, useEffect } from 'react';
import { Edit, Plus, Trash2, X, Save, ChevronUp, ChevronDown } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  default_for_tasks?: string[];
  contextWindow?: number;
  supports_vision?: boolean;
  taskDeterminationModel?: boolean;
  task_determination_priority?: number;
  category?: string;
  ranked_position?: number;
  date_of_model?: string;
  last_updated?: string;
  normalizedCost?: number;
  
  // Custom endpoint fields
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customApiKeyEncrypted?: string;
  customHeaders?: Record<string, string>;
  [key: string]: any;
}

interface EditModalProps {
  model: AIModel | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: AIModel) => void;
  isCreating: boolean;
  isAdmin: boolean;
}

function EditModal({ model, isOpen, onClose, onSave, isCreating, isAdmin }: EditModalProps) {
  const [editedModel, setEditedModel] = useState<AIModel | null>(null);
  const [error, setError] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (isCreating) {
        setEditedModel({
          id: '',
          providerModelId: '',
          name: '',
          provider: isAdmin ? 'openrouter' : 'custom',
          description: '',
          price_per_token: isAdmin ? '' : 'Free',
          capabilities: {
            webSearch: 0,
            enhancedReasoning: 0,
            reasoning: 0,
            coding: 0,
            creativity: 0,
            conversational: 0,
            speed: 0,
            imageGeneration: 0
          },
          default_for_tasks: [],
          contextWindow: 4096,
          supports_vision: false,
          taskDeterminationModel: false,
          task_determination_priority: 0,
          category: '',
          ranked_position: 999,
          date_of_model: '',
          last_updated: new Date().toISOString(),
          normalizedCost: 0,
          requires_premium: false,
          isCustomEndpoint: !isAdmin,
          customEndpointUrl: '',
          customApiKey: '',
          customHeaders: {}
        });
      } else {
        // Ensure capabilities and other JSON fields are properly parsed
        const processedModel = model ? { 
          ...model,
          capabilities: typeof model.capabilities === 'string' 
            ? JSON.parse(model.capabilities) 
            : model.capabilities || {},
          default_for_tasks: typeof model.default_for_tasks === 'string'
            ? JSON.parse(model.default_for_tasks)
            : model.default_for_tasks || [],
          customHeaders: typeof model.customHeaders === 'string'
            ? JSON.parse(model.customHeaders)
            : model.customHeaders || {}
        } : null;
        setEditedModel(processedModel);
      }
      setError('');
    }
  }, [isOpen, model, isCreating, isAdmin]);

  const handleFieldChange = (field: string, value: any) => {
    if (!editedModel) return;
    
    let processedValue = value;
    
    if (field === 'capabilities' || field === 'default_for_tasks' || field === 'customHeaders') {
      try {
        processedValue = typeof value === 'string' ? JSON.parse(value) : value;
      } catch {
        processedValue = value;
      }
    } else if (['contextWindow', 'task_determination_priority'].includes(field)) {
      processedValue = parseInt(value) || 0;
    } else if (field === 'ranked_position') {
      // Handle ranked_position specially to allow empty values
      processedValue = value === '' ? 999 : parseInt(value) || 999;
    } else if (field === 'normalizedCost') {
      processedValue = parseFloat(value) || 0;
    } else if (['requires_premium', 'supports_vision', 'taskDeterminationModel'].includes(field)) {
      processedValue = value === 'true' || value === true;
    }

    const updates: Partial<AIModel> = { [field]: processedValue };
    
    // Auto-generate ID from providerModelId
    if (field === 'providerModelId') {
      updates.id = processedValue;
    }
    
    // Handle provider changes
    if (field === 'provider') {
      updates.isCustomEndpoint = processedValue === 'custom';
      if (processedValue === 'custom') {
        updates.price_per_token = 'Free';
      }
    }

    setEditedModel({
      ...editedModel,
      ...updates
    });
  };

  const handleSave = () => {
    if (!editedModel) return;
    
    if (!editedModel.name || !editedModel.providerModelId) {
      setError('Name and Provider Model ID are required');
      return;
    }
    
    if (editedModel.provider === 'custom' && (!editedModel.customEndpointUrl || !editedModel.customApiKey)) {
      setError('Custom endpoint URL and API key are required for custom providers');
      return;
    }
    
    onSave(editedModel);
  };

  if (!isOpen || !editedModel) return null;

  const Tooltip = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <div className="relative inline-flex items-center gap-2">
      {children}
      <div 
        className="relative"
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
      >
        <span className="inline-flex items-center justify-center w-4 h-4 text-xs text-blue-600 border border-blue-300 rounded-full cursor-help hover:bg-blue-50">
          i
        </span>
        {showTooltip === field && (
          <div className="absolute left-0 top-6 z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
            {tooltips[field]}
          </div>
        )}
      </div>
    </div>
  );
  const tooltips: Record<string, string> = {
    providerModelId: 'Unique identifier used by the provider (e.g., gpt-4, claude-3-sonnet)',
    capabilities: 'Capability scores 0-5 for different task types',
    contextWindow: 'Maximum number of tokens the model can process',
    task_determination_priority: 'Priority level for task determination (higher = more preferred)',
    default_for_tasks: 'Task types this model is default for',
    price_per_token: 'Pricing format: "$0.50/1M tokens" or "free"',
    ranked_position: 'Global ranking position (1 = best)',
    normalizedCost: 'Cost per 1M tokens for comparison'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {isCreating ? 'Create New Model' : 'Edit Model'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {!isAdmin && (
          <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-800 rounded">
            <p className="font-semibold">Note:</p>
            <ul className="list-disc list-inside text-sm mt-1">
              <li>You can only add custom endpoint models (using your own API keys)</li>
              <li>All custom models are set to "Free" pricing</li>
              <li>Your models are only visible to you</li>
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <Tooltip field="providerModelId">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider Model ID *
              </label>
            </Tooltip>
            <input
              type="text"
              value={editedModel.providerModelId || ''}
              onChange={(e) => handleFieldChange('providerModelId', e.target.value)}
              placeholder="gpt-4-turbo"
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model Name *</label>
            <input
              type="text"
              value={editedModel.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="GPT-4 Turbo"
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
            <select
              value={editedModel.provider || (isAdmin ? 'openrouter' : 'custom')}
              onChange={(e) => handleFieldChange('provider', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={!isAdmin}
            >
              {isAdmin && <option value="openrouter">OpenRouter</option>}
              {isAdmin && <option value="google">Google</option>}
              <option value="custom">Custom Endpoint</option>
            </select>
            {!isAdmin && (
              <p className="text-xs text-gray-500 mt-1">Only custom endpoints available for regular users</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={editedModel.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Latest GPT-4 model with improved performance"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <Tooltip field="price_per_token">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price per Token
              </label>
            </Tooltip>
            <input
              type="text"
              value={!isAdmin ? 'Free' : (editedModel.price_per_token || '')}
              onChange={(e) => handleFieldChange('price_per_token', e.target.value)}
              placeholder="$10/1M tokens"
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={!isAdmin}
              readOnly={!isAdmin}
            />
            {!isAdmin && (
              <p className="text-xs text-gray-500 mt-1">Custom models are always free for regular users</p>
            )}
          </div>

          <div>
            <Tooltip field="contextWindow">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Context Window
              </label>
            </Tooltip>
            <input
              type="number"
              value={editedModel.contextWindow || 4096}
              onChange={(e) => handleFieldChange('contextWindow', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <Tooltip field="ranked_position">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ranked Position
              </label>
            </Tooltip>
            <input
              type="text"
              value={editedModel.ranked_position || ''}
              onChange={(e) => handleFieldChange('ranked_position', e.target.value)}
              placeholder="20"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input
              type="text"
              value={editedModel.category || ''}
              onChange={(e) => handleFieldChange('category', e.target.value)}
              placeholder="general"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supports Vision</label>
            <select
              value={editedModel.supports_vision ? 'true' : 'false'}
              onChange={(e) => handleFieldChange('supports_vision', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>

          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Determination Model</label>
              <select
                value={editedModel.taskDeterminationModel ? 'true' : 'false'}
                onChange={(e) => handleFieldChange('taskDeterminationModel', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          )}

          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requires Premium</label>
              <select
                value={editedModel.requires_premium ? 'true' : 'false'}
                onChange={(e) => handleFieldChange('requires_premium', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          )}

          {isAdmin && (
            <div>
              <Tooltip field="task_determination_priority">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Determination Priority
                </label>
              </Tooltip>
              <input
                type="number"
                value={editedModel.task_determination_priority || 0}
                onChange={(e) => handleFieldChange('task_determination_priority', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          )}
        </div>

        <div className="mb-6">
          <Tooltip field="capabilities">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capabilities (0-5 scale)
            </label>
          </Tooltip>
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md border border-gray-300">
            {Object.entries(editedModel.capabilities || {}).map(([key, value]) => (
              <div key={key} className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
                  {key.replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').trim().slice(1)}:
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="1"
                  value={value as number}
                  onChange={(e) => {
                    const newCapabilities = { ...editedModel.capabilities };
                    newCapabilities[key] = parseFloat(e.target.value) || 0;
                    handleFieldChange('capabilities', newCapabilities);
                  }}
                  className="w-20 p-2 border border-gray-300 rounded-md text-center font-semibold"
                />
              </div>
            ))}
          </div>
        </div>

        {/* <div className="mb-6">
          <Tooltip field="default_for_tasks">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default for Tasks
            </label>
          </Tooltip>
          <textarea
            value={typeof editedModel.default_for_tasks === 'string'
              ? editedModel.default_for_tasks
              : JSON.stringify(editedModel.default_for_tasks || [], null, 2)}
            onChange={(e) => handleFieldChange('default_for_tasks', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm"
            rows={3}
            placeholder='["coding", "reasoning"]'
          />
        </div> */}

        {editedModel.provider === 'custom' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-lg font-medium text-blue-900 mb-4">Custom Endpoint Configuration</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">Endpoint URL *</label>
                <input
                  type="url"
                  value={editedModel.customEndpointUrl || ''}
                  onChange={(e) => handleFieldChange('customEndpointUrl', e.target.value)}
                  placeholder="https://api.example.com/v1"
                  className="w-full p-2 border border-blue-300 rounded-md"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">API Key *</label>
                <input
                  type="password"
                  value={editedModel.customApiKey || ''}
                  onChange={(e) => handleFieldChange('customApiKey', e.target.value)}
                  placeholder="sk-..."
                  className="w-full p-2 border border-blue-300 rounded-md"
                  required
                />
                <p className="text-xs text-blue-600 mt-1">Will be encrypted when saved</p>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-blue-700 mb-1">Custom Headers (JSON)</label>
              <textarea
                value={typeof editedModel.customHeaders === 'string'
                  ? editedModel.customHeaders
                  : JSON.stringify(editedModel.customHeaders || {}, null, 2)}
                onChange={(e) => handleFieldChange('customHeaders', e.target.value)}
                placeholder='{\n  "X-Custom-Header": "value"\n}'
                className="w-full p-2 border border-blue-300 rounded-md font-mono text-sm"
                rows={4}
              />
              <p className="text-xs text-blue-600 mt-1">Optional additional headers for requests</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Save size={16} />
            {isCreating ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Models() {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin || false;
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sortField, setSortField] = useState<string>('ranked_position');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [uniqueCapabilities, setUniqueCapabilities] = useState<string[]>([]);

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    if (models.length > 0) {
      const capabilitySet = new Set<string>();
      models.forEach(model => {
        if (model.capabilities) {
          Object.keys(model.capabilities).forEach(cap => capabilitySet.add(cap));
        }
      });
      setUniqueCapabilities(Array.from(capabilitySet).sort());
      
      const sorted = [...models].sort((a: AIModel, b: AIModel) => {
        let aVal: any;
        let bVal: any;
        
        if (sortField.startsWith('capability.')) {
          const capabilityName = sortField.replace('capability.', '');
          aVal = a.capabilities?.[capabilityName] || 0;
          bVal = b.capabilities?.[capabilityName] || 0;
        } else {
          aVal = a[sortField as keyof AIModel];
          bVal = b[sortField as keyof AIModel];
        }
        
        if (aVal === undefined || aVal === null) aVal = sortField === 'ranked_position' ? 999 : '';
        if (bVal === undefined || bVal === null) bVal = sortField === 'ranked_position' ? 999 : '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
      setModels(sorted);
    }
  }, [sortField, sortDirection]);

  const loadModels = async () => {
    try {
      setLoading(true);
      setError('');

      // Load both global models and user's custom models
      const [globalResponse, customResponse] = await Promise.all([
        apiService.get('/portal/models/raw'),
        apiService.get('/portal/models/custom')
      ]);

      const globalModels = Array.isArray(globalResponse.data) ? globalResponse.data :
                          Array.isArray(globalResponse) ? globalResponse : [];
      const customModels = Array.isArray(customResponse.data) ? customResponse.data :
                          Array.isArray(customResponse) ? customResponse : [];

      // Combine global and custom models
      const allModels = [...globalModels, ...customModels];

      const capabilitySet = new Set<string>();
      allModels.forEach((model: AIModel) => {
        if (model.capabilities) {
          Object.keys(model.capabilities).forEach(cap => capabilitySet.add(cap));
        }
      });
      setUniqueCapabilities(Array.from(capabilitySet).sort());

      const sortedModels = allModels.sort((a: AIModel, b: AIModel) => {
        const rankA = a.ranked_position || 999;
        const rankB = b.ranked_position || 999;
        return rankA - rankB;
      });

      setModels(sortedModels);
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          sortDirection === 'asc' ? 
            <ChevronUp size={14} className="text-blue-600" /> : 
            <ChevronDown size={14} className="text-blue-600" />
        )}
      </div>
    </th>
  );

  const handleEdit = (model: AIModel) => {
    setEditingModel(model);
    setIsCreating(false);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingModel(null);
    setIsCreating(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (model: AIModel) => {
    if (!window.confirm(`Delete model "${model.name}"?`)) return;

    try {
      await apiService.delete(`/portal/models/${model.id}`);
      await loadModels();
    } catch (err: any) {
      setError(err.message || 'Failed to delete model');
    }
  };

  const handleSave = async (model: AIModel) => {
    try {
      if (isCreating) {
        await apiService.post('/portal/models', model);
      } else {
        await apiService.put(`/portal/models/${model.id}`, model);
      }
      setIsModalOpen(false);
      await loadModels();
    } catch (err: any) {
      setError(err.message || 'Failed to save model');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Model Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Sorted by: <span className="font-medium">
              {sortField.startsWith('capability.')
                ? sortField.replace('capability.', '').charAt(0).toUpperCase() + sortField.replace('capability.', '').slice(1) + ' (Capability)'
                : sortField.replace('_', ' ')}
            </span>
            <span className="ml-1">
              {sortDirection === 'asc' ? '↑' : '↓'}
            </span>
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus size={16} />
          Add Model
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader field="ranked_position">Rank</SortableHeader>
                <SortableHeader field="name">Name</SortableHeader>
                <SortableHeader field="provider">Provider</SortableHeader>
                <SortableHeader field="providerModelId">Model ID</SortableHeader>
                <SortableHeader field="price_per_token">Price</SortableHeader>
                <SortableHeader field="category">Category</SortableHeader>
                <SortableHeader field="contextWindow">Context Window</SortableHeader>
                
                {uniqueCapabilities.map(capability => (
                  <SortableHeader key={capability} field={`capability.${capability}`}>
                    {capability.charAt(0).toUpperCase() + capability.slice(1)}
                  </SortableHeader>
                ))}
                
                <SortableHeader field="supports_vision">Vision</SortableHeader>
                <SortableHeader field="taskDeterminationModel">Task Det.</SortableHeader>
                <SortableHeader field="requires_premium">Premium</SortableHeader>
                <SortableHeader field="date_of_model">Date</SortableHeader>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {models.map((model) => (
                <tr key={model.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {model.ranked_position || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-xs truncate">
                    {model.name}
                    {model.provider === 'custom' && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Custom
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.provider}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                    {model.providerModelId}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.price_per_token || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.category || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.contextWindow || 'N/A'}
                  </td>
                  
                  {uniqueCapabilities.map(capability => (
                    <td key={capability} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                        model.capabilities?.[capability] 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        {model.capabilities?.[capability] || '-'}
                      </span>
                    </td>
                  ))}
                  
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.supports_vision ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.supports_vision ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.taskDeterminationModel ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.taskDeterminationModel ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.requires_premium ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {model.requires_premium ? 'Premium' : 'Free'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.date_of_model || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(model)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit model"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(model)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete model"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EditModal
        model={editingModel}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isCreating={isCreating}
        isAdmin={isAdmin}
      />
    </div>
  );
}
